import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Wand2, BarChart3, Gift, Puzzle, Camera, TrendingDown, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from 'shared/components/ui/Card';
import Badge from 'shared/components/ui/Badge';
import tryonImage from 'assets/landing/feature-tryon.jpg';
import compareImage from 'assets/landing/feature-compare.jpg';
import giftsImage from 'assets/landing/feature-gifts.jpg';
import extensionImage from 'assets/landing/feature-extension.jpg';
import searchImage from 'assets/landing/icon-ai-search.jpg';
import dealsImage from 'assets/landing/icon-stores.jpg';
import './FeaturesSection.scss';

const FeaturesSection = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Wand2,
      title: t('feature_tryon_title', 'Try Fashion On You'),
      description: t(
        'feature_tryon_desc',
        'Upload your photo to see how fashion items look on you instantly. Try on clothes from saved Likiee products or any image with AI-powered virtual try-on technology.',
      ),
      image: tryonImage,
      imageAlt: t(
        'feature_tryon_alt',
        'Virtual try-on technology showing clothing on real people using AI augmented reality',
      ),
      highlight: 'new',
      link: '/virtual-try-on',
    },
    {
      icon: BarChart3,
      title: t('feature_compare_title', 'AI Compare Products'),
      description: t(
        'feature_compare_desc',
        'Get instant AI-powered analysis comparing quality, price, features, reviews, and more. Make smarter shopping decisions with comprehensive side-by-side comparisons.',
      ),
      image: compareImage,
      imageAlt: t('feature_compare_alt', 'AI-powered product comparison showing detailed analysis of multiple items'),
      highlight: 'popular',
      link: '/ai-comparison',
    },
    {
      icon: Gift,
      title: t('feature_gifts_title', "Gifts They'll Love"),
      description: t(
        'feature_gifts_desc',
        "Create wishlists that friends and family can access and contribute to. Navigate through friends' lists to select the perfect present, making celebrations personal and gifts meaningful.",
      ),
      image: giftsImage,
      imageAlt: t('feature_gifts_alt', 'Shared wishlist interface for gift-giving and collaborative shopping'),
      highlight: null,
      link: '/shared-wishlists',
    },
    {
      icon: Puzzle,
      title: t('feature_extension_title', 'Likiee Button Extension'),
      description: t(
        'feature_extension_desc',
        'Add products to your Likiee list from any online retailer with just one click. Our smart browser extension seamlessly integrates, keeping all items organized and accessible.',
      ),
      image: extensionImage,
      imageAlt: t('feature_extension_alt', 'Browser extension one-click save button for easy product organization'),
      highlight: null,
      link: '#extension',
    },
    {
      icon: Camera,
      title: t('feature_search_title', 'Snap, Save, Discover'),
      description: t(
        'feature_search_desc',
        'Take a photo or screenshot and upload to Likiee. Our AI platform will save, organize, and find similar items or better offers for you across thousands of stores.',
      ),
      image: searchImage,
      imageAlt: t('feature_search_alt', 'Visual search technology scanning and identifying products from photos'),
      highlight: null,
      link: '#visual-search',
    },
    {
      icon: TrendingDown,
      title: t('feature_deals_title', 'Discover Better Deals'),
      description: t(
        'feature_deals_desc',
        "Likiee doesn't just save your items—it actively finds better deals and similar products. Expand your shopping horizons with AI-powered recommendations and price tracking.",
      ),
      image: dealsImage,
      imageAlt: t('feature_deals_alt', 'Price comparison and deal discovery showing savings across multiple retailers'),
      highlight: null,
      link: '/price-tracking',
    },
  ];

  return (
    <section id='features' className='features-section'>
      <div className='features-section__container'>
        <header className='features-section__header'>
          <div className='features-section__badge'>
            <Sparkles size={16} aria-hidden='true' />
            <span className='features-section__badge-text'>{t('features_badge', 'Complete Shopping Suite')}</span>
          </div>
          <h2 className='features-section__title'>
            {t('features_title_line1', 'Everything You Need in')}{' '}
            <span className='features-section__title--gradient'>{t('features_title_line2', 'One Platform')}</span>
          </h2>
          <p className='features-section__subtitle'>
            {t(
              'features_subtitle',
              'From virtual try-on to AI comparison, Likiee transforms your entire shopping experience',
            )}
          </p>
        </header>

        {/* Features Grid */}
        <div className='features-section__grid'>
          {features.map((feature, index) => (
            <article key={index} className='features-section__card-wrapper'>
              <Card hover={true} className='features-section__card'>
                <CardContent>
                  <div className='features-section__card-image-wrapper'>
                    {feature.highlight && (
                      <div className='features-section__card-badge'>
                        <Badge variant={feature.highlight}>{t(`badge_${feature.highlight}`, feature.highlight)}</Badge>
                      </div>
                    )}
                    <img
                      src={feature.image}
                      alt={feature.imageAlt}
                      className='features-section__card-image'
                      loading='lazy'
                    />
                  </div>

                  <div className='features-section__card-icon'>
                    <feature.icon size={24} aria-hidden='true' />
                  </div>

                  <h3 className='features-section__card-title'>{feature.title}</h3>
                  <p className='features-section__card-description'>{feature.description}</p>

                  <Link to={feature.link} className='features-section__card-link'>
                    {t('feature_learn_more', 'Learn More')}
                    <ArrowRight size={16} className='features-section__card-arrow' />
                  </Link>
                </CardContent>
              </Card>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
